import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["children", "className", "kind", "block", "slim", "responsive", "centerIcon", "leftIcon", "rightIcon", "spacing", "elementType", "type", "size", "radius", "fullWidth", "display", "buttonStyles", "noBox", "label", "telLeftIcon", "telRightIcon", "openInChildWindow", "anchorLabel", "anchorClassName", "tooltipLabel", "aria-describedby", "onClick", "smoothBoldLabelTransition", "active", "noAriaDescribedby"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

/* eslint-disable complexity */
import { useTheme } from "@emotion/react";
import clsx from "clsx";
import PropTypes from "prop-types";
import React from "react";
import { POPUP_PREFIX } from "../../common/constants";
import { validIcon } from "../../common/proptypes";
import { safeMerge } from "../../common/utilities";
import { BUTTON_BASE_CLASS, BUTTON_CENTER_ICON_CLASS, BUTTON_CHROMELESS_CLASS, BUTTON_LABEL_CLASS, BUTTON_LEFT_ICON_CLASS, BUTTON_RIGHT_ICON_CLASS, BUTTON_SIMPLE_CLASS } from "../../themes/common/classes";
import Box from "../Box/Box";
import Tooltip from "../Tooltip/Tooltip";
import extractTheme from "./extractTheme";
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";

const renderLabel = _ref => {
  let {
    buttonLabel,
    smoothBoldLabelTransition,
    cssLabel
  } = _ref;
  const isSmooth = smoothBoldLabelTransition && typeof buttonLabel === "string";
  return buttonLabel ? _jsx("span", {
    className: BUTTON_LABEL_CLASS,
    css: isSmooth ? cssLabel : null,
    "data-text": isSmooth ? buttonLabel : null,
    children: buttonLabel
  }) : null;
};

const BaseButton = /*#__PURE__*/React.forwardRef((_ref2, ref) => {
  let {
    children,
    className,
    kind,
    block,
    slim,
    responsive,
    centerIcon,
    leftIcon,
    rightIcon,
    spacing,
    elementType,
    type: typeProp,
    size,
    radius,
    fullWidth,
    display,
    // eslint-disable-line react/prop-types
    buttonStyles,
    noBox,
    label,
    // eslint-disable-line react/prop-types
    telLeftIcon,
    // eslint-disable-line react/prop-types
    telRightIcon,
    // eslint-disable-line react/prop-types
    openInChildWindow,
    // eslint-disable-line react/prop-types
    anchorLabel,
    // eslint-disable-line react/prop-types
    anchorClassName,
    // eslint-disable-line react/prop-types
    tooltipLabel: tooltipLabelProp,
    // eslint-disable-line react/prop-types
    "aria-describedby": ariaDescribedby,
    onClick,
    smoothBoldLabelTransition,
    active,
    noAriaDescribedby
  } = _ref2,
      otherProps = _objectWithoutProperties(_ref2, _excluded);

  const theme = useTheme();
  const tooltipLabel = tooltipLabelProp || otherProps["aria-label"] || children || "Tooltip";
  const buttonLabel = label || children || anchorLabel;
  const Element = elementType;
  const tooltipRequired = centerIcon || responsive && (leftIcon || rightIcon);
  const {
    cssBox,
    cssRoot,
    cssLabel
  } = extractTheme({
    theme,
    kind,
    slim,
    block,
    centerIcon: Boolean(centerIcon),
    leftIcon: Boolean(leftIcon),
    rightIcon: Boolean(rightIcon),
    radius,
    display,
    responsive,
    size,
    fullWidth
  });
  const elementClassName = clsx({
    [BUTTON_CHROMELESS_CLASS]: kind === "chromeless",
    [BUTTON_SIMPLE_CLASS]: kind === "simple",
    [BUTTON_BASE_CLASS]: true,
    [anchorClassName]: anchorClassName && anchorClassName !== "",
    [className]: noBox && className
  });

  if (elementType === "a" && typeProp === "button") {
    typeProp = null;
  }
  /**
   * If the Button is the trigger prop of a Tooltip, don't wrap it with another
   * Tooltip.This could cause props from the original wrapping Tooltip to be lost.
   */


  const fromTooltip = className && className.slice(0, POPUP_PREFIX.length) === POPUP_PREFIX;
  const combinedButtonStyles = safeMerge(cssRoot, buttonStyles);
  /**
   * Internal hack to handle Safari (iOS and desktop).
   *
   * Safari does not consider a <button> as a focusable
   * element, therefore, when one <button> is clicked
   * in Safari, we cannot rely on activeElement to figure
   * out what triggered the action (this is the way Popup
   * works: it finds activeElement when opening, so that when
   * it closes, it can automatically move the focus back
   * to the triggering button).
   *
   * But because Safari does not mark the button as "focused",
   * then Popup does not find an activeElement (actually it
   * does find one, but it's the body, not the button!).
   *
   * This is where this hack comes in: we intercept a click,
   * then we are "forcing" a focus, before actually executing
   * the click handler. It has no impact on browsers that
   * already apply the focus themselves, but it helps poor
   * old cranky Safari.
   *
   */

  const internalClick = e => {
    var _e$currentTarget;

    typeof (e === null || e === void 0 ? void 0 : (_e$currentTarget = e.currentTarget) === null || _e$currentTarget === void 0 ? void 0 : _e$currentTarget.focus) === "function" && e.currentTarget.focus();
    typeof onClick === "function" && onClick(e);
  };

  const attrs = _objectSpread(_objectSpread({}, otherProps), {}, {
    "aria-pressed": active
  });

  const buttonElement = centerIcon ? _jsx(Element, _objectSpread(_objectSpread({
    className: elementClassName,
    ref: ref,
    css: combinedButtonStyles,
    type: typeProp,
    "aria-describedby": ariaDescribedby
  }, attrs), {}, {
    onClick: internalClick,
    children: _jsx("span", {
      className: BUTTON_CENTER_ICON_CLASS,
      children: centerIcon
    })
  })) : _jsxs(Element, _objectSpread(_objectSpread({
    className: elementClassName,
    ref: ref,
    css: combinedButtonStyles,
    type: typeProp,
    "aria-describedby": ariaDescribedby
  }, attrs), {}, {
    onClick: internalClick,
    children: [leftIcon ? _jsx("span", {
      className: BUTTON_LEFT_ICON_CLASS,
      children: leftIcon
    }) : null, telLeftIcon ? _jsx("span", {
      className: BUTTON_LEFT_ICON_CLASS,
      children: telLeftIcon
    }) : null, openInChildWindow ? // eslint-disable-next-line jsx-a11y/aria-role
    _jsxs("span", {
      role: "text",
      children: [renderLabel({
        buttonLabel,
        smoothBoldLabelTransition,
        cssLabel
      }), _jsx("span", {
        className: BUTTON_RIGHT_ICON_CLASS,
        children: openInChildWindow
      })]
    }) : renderLabel({
      buttonLabel,
      smoothBoldLabelTransition,
      cssLabel
    }), telRightIcon ? _jsx("span", {
      className: BUTTON_RIGHT_ICON_CLASS,
      children: telRightIcon
    }) : null, rightIcon ? _jsx("span", {
      className: BUTTON_RIGHT_ICON_CLASS,
      children: rightIcon
    }) : null]
  }));
  const element = fromTooltip || !tooltipRequired ? buttonElement : _jsx(Tooltip, {
    noAriaDescribedby: noAriaDescribedby,
    className: className,
    label: tooltipLabel,
    trigger: buttonElement
  });
  return noBox ? element : _jsx(Box, {
    spacing: spacing,
    theme: theme,
    className: className,
    css: cssBox,
    children: element
  });
});
BaseButton.propTypes = process.env.NODE_ENV !== "production" ? {
  /** Set the button active/pressed state */
  active: PropTypes.bool,

  /** Used to determine if the parent Button is a trigger prop of a Tooltip */
  "aria-describedby": PropTypes.string,

  /** Stretch the button to take the full width of its container up to 300px. */
  block: PropTypes.bool,

  /**
   * Button style (CSS-in-JS).
   * Example:
   * {
   *   fontFamily: "arial",
   *   fontSize: pxToRem(14),
   *   maxWidth: pxToRem(200),
   *   borderRadius: 3,
   *   padding: pxToRem(10),
   *   border: "1px solid #8d8d8d",
   *   backgroundColor: "red",
   * }
   */
  buttonStyles: PropTypes.shape(),

  /**
   * An SVGIcon component to create an icon-only-button. When used,
   * children are ignored. For accessibility reasons, you need
   * to also provide an aria-label.
   */
  centerIcon: validIcon,

  /** The body of the button. */
  children: PropTypes.node,
  elementType: PropTypes.oneOf(["button", "a"]),
  fullWidth: PropTypes.bool,

  /** The style of button. */
  kind: PropTypes.oneOf(["primary", "standard", "chromeless", "simple"]),

  /** An SVGIcon component to place on the left of the button. */
  leftIcon: validIcon,
  // To prevent `aria-describedby` from being added by tooltip to trigger element.
  noAriaDescribedby: PropTypes.bool,

  /**
   * Whether to wrap the button with `Box` component or not.
   * By default `Box` component is wrapped.
   */
  noBox: PropTypes.bool,
  onClick: PropTypes.func,
  radius: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(["small", "medium", "large"])]),

  /**
   * Span 100% width of the parent at lower breakpoint when used without an icon.
   * When combined with an icon, the button does not extend, but the label gets
   * hidden at lower breakpoints.
   */
  responsive: PropTypes.bool,

  /** An SVGIcon component to place on the right of the button. */
  rightIcon: validIcon,
  size: PropTypes.oneOf(["small", "medium", "large"]),

  /** Smaller profile. Does not apply to "simple". */
  slim: PropTypes.bool,

  /**
   * Private prop that should be enabled if:
   * - the label is pure text (no icon, etc.)
   * - on some action (selected, focus, etc), the font weight
   *   must change (for example, the NavBarItems).
   */
  smoothBoldLabelTransition: PropTypes.bool,

  /**
   * Custom spacing for the component. See the
   * "Customization -> Spacing" section.
   */
  spacing: PropTypes.oneOfType([PropTypes.number, PropTypes.shape()]),

  /** The HTML type of the button. */
  type: PropTypes.oneOf(["button", "submit", "reset"])
} : {};
BaseButton.defaultProps = {
  "aria-describedby": null,
  active: null,
  block: false,
  buttonStyles: {},
  centerIcon: null,
  children: null,
  className: null,
  elementType: "button",
  fullWidth: false,
  href: null,
  kind: "primary",
  leftIcon: null,
  noAriaDescribedby: false,
  noBox: false,
  onClick: null,
  radius: "small",
  responsive: false,
  rightIcon: null,
  size: "medium",
  slim: false,
  smoothBoldLabelTransition: false,
  spacing: {},
  type: "button"
};
BaseButton.displayName = "BaseButton";
export default BaseButton;